import clsx from 'clsx';
import Button from 'components/form/Button';
import React, { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSidebarStore from 'state/useSidebar';
import useUserStore from 'state/userStore';
import HeadBar from 'components/layouts/dashboard/HeadBar';

interface DashboardLayoutProps {
  children: ReactNode;
}

const NavItems = [
  {
    title: 'Users',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-6 w-6"
      >
        <path
          fillRule="evenodd"
          d="M3 6a3 3 0 013-3h2.25a3 3 0 013 3v2.25a3 3 0 01-3 3H6a3 3 0 01-3-3V6zm9.75 0a3 3 0 013-3H18a3 3 0 013 3v2.25a3 3 0 01-3 3h-2.25a3 3 0 01-3-3V6zM3 15.75a3 3 0 013-3h2.25a3 3 0 013 3V18a3 3 0 01-3 3H6a3 3 0 01-3-3v-2.25zm9.75 0a3 3 0 013-3H18a3 3 0 013 3V18a3 3 0 01-3 3h-2.25a3 3 0 01-3-3v-2.25z"
          clipRule="evenodd"
        />
      </svg>
    ),
    link: '/admin/users',
  },
  {
    title: 'Accounts',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-6 w-6"
      >
        <path d="M2.273 5.625A4.483 4.483 0 015.25 4.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0018.75 3H5.25a3 3 0 00-2.977 2.625zM2.273 8.625A4.483 4.483 0 015.25 7.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0018.75 6H5.25a3 3 0 00-2.977 2.625zM5.25 9a3 3 0 00-3 3v6a3 3 0 003 3h13.5a3 3 0 003-3v-6a3 3 0 00-3-3H15a.75.75 0 00-.75.75 2.25 2.25 0 01-4.5 0A.75.75 0 009 9H5.25z" />
      </svg>
    ),
    link: '/admin/accounts',
  },
  {
    title: 'Transactions',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-6 w-6"
      >
        <path d="M12 7.5a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5z" />
        <path
          fillRule="evenodd"
          d="M1.5 4.875C1.5 3.839 2.34 3 3.375 3h17.25c1.035 0 1.875.84 1.875 1.875v9.75c0 1.036-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 011.5 14.625v-9.75zM8.25 9.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM18.75 9a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V9.75a.75.75 0 00-.75-.75h-.008zM4.5 9.75A.75.75 0 015.25 9h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9.75z"
          clipRule="evenodd"
        />
        <path d="M2.25 18a.75.75 0 000 1.5c5.4 0 10.63.722 15.6 2.075 1.19.324 2.4-.558 2.4-1.82V18.75a.75.75 0 00-.75-.75H2.25z" />
      </svg>
    ),
    link: '/admin/transactions',
  },
];

const AdminLayout = ({ children }: DashboardLayoutProps) => {
  const { isOpen, setIsOpen } = useSidebarStore();
  const { clearUser } = useUserStore();
  const { pathname } = useLocation();

  const handleClose = (e: any) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    clearUser();
    window.localStorage.removeItem('token');
    navigate('/');
  };

  return (
    <div className="flex overflow-hidden bg-white md:h-screen">
      <div
        className={clsx(
          'overflow-x-hidden bg-[#5f5f5f1a] transition-all lg:w-72 lg:overflow-x-visible',
          isOpen
            ? 'fixed inset-y-0 z-40 w-full lg:static'
            : 'hidden w-0 lg:block'
        )}
      >
        <div
          className="brand-border scrollbar relative flex h-full w-3/4 flex-none flex-col overflow-y-auto border-r bg-white md:w-72"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          <div className="sticky inset-x-0 top-0 mb-2 space-y-5 bg-white px-3 pb-3 pt-5">
            <div className="flex items-center justify-between">
              <div className="flex items-end gap-x-1">
                <div className="h-[50px] w-[50px]">
                  <img
                    src="/assets/logo.png"
                    alt="app-logo"
                    className="h-full w-full object-cover"
                  />
                </div>

                <p className="mb-1 font-mono text-xl font-bold">Blockchain</p>
              </div>{' '}
              <Button onClick={() => setIsOpen(false)} className="lg:hidden">
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </span>
              </Button>
            </div>
          </div>
          <div className="my-10 flex flex-col justify-center gap-y-4 px-3">
            {NavItems.map((item, index) => (
              <Button key={index} className="w-full" onClick={handleClose}>
                <Link to={`${item.link}` || '/'}>
                  <div
                    className={clsx(
                      item.link === pathname
                        ? 'border-black bg-black text-white'
                        : 'hover:bg-zinc-100',
                      'flex w-full cursor-pointer items-center space-x-5 rounded-md py-3 pl-10'
                    )}
                  >
                    <span
                      className={clsx(
                        item.link === pathname
                          ? 'fill-white'
                          : 'text-brand-blue'
                      )}
                    >
                      {item.icon}
                    </span>
                    <p>{item.title}</p>
                  </div>
                </Link>
              </Button>
            ))}
          </div>
          <br />

          <div className="absolute inset-x-0 bottom-3 flex flex-col justify-center gap-y-4 px-3">
            <div className="w-full">
              <Button
                className="h-full w-full !px-0 text-red-500 hover:!brightness-100"
                onClick={handleLogout}
              >
                <div className="flex w-full cursor-pointer items-center space-x-5 rounded-md py-3 pl-10 hover:bg-zinc-100">
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-6 w-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                      />
                    </svg>
                  </span>
                  <span>Logout</span>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-1 flex-col">
        <HeadBar />
        <div className="scrollbar mt-[70px] overflow-y-auto bg-white lg:mt-0">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
